import React, { useState, useEffect } from 'react';
import FieldContainer from 'components/layout/FieldContainer';
import { getValidationSchema } from 'utils/validationProfExpSchemaGenerator';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import { Alert, Snackbar } from '@mui/material';
import styles from '../ProfBackground.module.scss';
import ExperienceItemEditing from './ExperienceItemEditing';
import ProfBackgroundItem from '../ProfBackgroundItem';
import { DELETE_USER_EXPERIENCE, SAVE_USER_EXPERIENCE } from '../../../../api';
import moment from 'moment';
import PartialLoader from 'components/layout/PartialLoader';

const EMPTY_TEXT = 'Add your experience';
const EXPERIENCE = 'Experience';

const Notification = React.forwardRef((props, ref) => (
  <Alert elevation={6} ref={ref} variant="filled" {...props} />
));

const Experience = ({ userProps, isCurrentUser, setUserData }) => {
  const [isEditing, setIsEditing] = useState(false);
  const validationSchema = getValidationSchema('experience');
  const formOptions = { resolver: yupResolver(validationSchema) };
  const formMethods = useForm(formOptions, { mode: 'all' });
  const [experiences, setExperiences] = useState([]);
  const [experiencesList, setExperiencesList] = useState([]);
  const [saveUserExperience] = useMutation(SAVE_USER_EXPERIENCE);
  const [deleteUserExperience] = useMutation(DELETE_USER_EXPERIENCE);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    control,
  } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'experience',
    keyName: 'key',
  });

  useEffect(() => {
    if (!isEditing) {
      setValue('experience', []);
    } else if (experiencesList && experiencesList.length) {
      experiencesList.map((item) => {
        append({
          id: item.id,
          title: item.title,
          company_name: item.company_name,
          start_date: moment(item.start_date).toISOString(),
          end_date: moment(item.end_date).toISOString(),
          is_present: item.is_present,
        });
        return item;
      });
    }
  }, [isEditing]);

  useEffect(() => {
    if (userProps?.experiences && userProps.experiences.length) {
      setExperiencesList(userProps.experiences);
    } else {
      setExperiencesList([]);
    }
  }, [userProps]);

  const handleAdd = () => {
    append({
      title: '',
      company_name: '',
      start_date: null,
      end_date: null,
      is_present: true,
    });
  };

  const handleDelete = async (index) => {
    if (fields[index].id) {
      const res = await deleteUserExperience({ variables: { id: fields[index].id } });
      if (res.data) {
        remove(index);
        const newExperiencesList = experiencesList.filter((item, i) => index !== i);
        setUserData({ ...userProps, experiences: newExperiencesList });
        return true;
      }
    }
    remove(index);
    return true;
  };

  const handleNotificationClose = () => {
    setIsNotificationOpen(false);
  };

  const onFormSubmit = async (data) => {
    setLoading(true);
    const experienceList = [];
    data.experience.map((item) => {
      const itemData = { ...item };
      itemData.start_date = moment(item.start_date).format('YYYY-MM-DD');
      if (itemData.end_date) {
        itemData.end_date = moment(item.end_date).format('YYYY-MM-DD');
      }
      experienceList.push(itemData);
      return item;
    });
    const userExperience = await saveUserExperience({ variables: { input: experienceList } });
    if (userExperience?.data?.saveUserExperience?.experiences) {
      setIsNotificationOpen(true);
      setIsEditing(false);
      setUserData({
        ...userProps,
        experiences: userExperience.data.saveUserExperience.experiences,
      });
    }
    setLoading(false);
  };

  return (
    <>
      {isCurrentUser ? (
        <FieldContainer
          key="experience"
          objKey="experience"
          title={EXPERIENCE}
          className={styles.contentBox}
          isCurrentUser={isCurrentUser}
          setIsEditingProfile={setIsEditing}
          isEditingProfile={isEditing}
          addProf={handleAdd}
          formId="experience"
          containEditFieldButton
          isProfBackground
        >
          {experiencesList.length === 0 && !isEditing && (
            <div className={styles.emptyText}>{EMPTY_TEXT}</div>
          )}
          {fields.length > 0 || (experiencesList && experiencesList.length) ? (
            <FormProvider {...formMethods}>
              <form id="experience" onSubmit={handleSubmit(onFormSubmit)}>
                {isEditing &&
                  fields?.map((exp, index) => (
                    <ExperienceItemEditing
                      key={exp.key}
                      formName="experience"
                      index={index}
                      profProps={exp}
                      formTitle="Experience"
                      data={experiences}
                      setData={setExperiences}
                      control={control}
                      handleDelete={handleDelete}
                      setValue={setValue}
                      errors={errors}
                      isNew={!exp.id}
                      experienceName={exp.title}
                      setError={setError}
                    />
                  ))}
                {!isEditing &&
                  experiencesList.map((exp, index) => (
                    <ProfBackgroundItem
                      key={exp.id}
                      index={index}
                      title={exp.title}
                      subTitle={exp.company_name}
                      timeInterval={`${moment(exp.start_date).format('MM.YYYY')} - ${
                        exp.end_date ? moment(exp.end_date).format('MM.YYYY') : 'Present'
                      }`}
                    />
                  ))}
              </form>
            </FormProvider>
          ) : null}
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={isNotificationOpen}
            onClose={handleNotificationClose}
            autoHideDuration={6000}
          >
            <Notification onClose={handleNotificationClose} severity="success">
              Experiences Updated Successfully!
            </Notification>
          </Snackbar>
        </FieldContainer>
      ) : experiencesList.length !== 0 ? (
        <FieldContainer
          key="experience"
          objKey="experience"
          title={EXPERIENCE}
          className={styles.contentBox}
          isCurrentUser={isCurrentUser}
          setIsEditingProfile={setIsEditing}
          isEditingProfile={isEditing}
          addProf={handleAdd}
          formId="experience"
          containEditFieldButton
          isProfBackground
        >
          {fields.length > 0 || (experiencesList && experiencesList.length) ? (
            <FormProvider {...formMethods}>
              <form id="experience" onSubmit={handleSubmit(onFormSubmit)}>
                {isEditing &&
                  fields?.map((exp, index) => (
                    <ExperienceItemEditing
                      key={exp.key}
                      formName="experience"
                      index={index}
                      profProps={exp}
                      formTitle="Experience"
                      data={experiences}
                      setData={setExperiences}
                      control={control}
                      handleDelete={handleDelete}
                      setValue={setValue}
                      errors={errors}
                      isNew={!exp.id}
                    />
                  ))}
                {!isEditing &&
                  experiencesList.map((exp, index) => (
                    <ProfBackgroundItem
                      key={exp.id}
                      index={index}
                      title={exp.title}
                      subTitle={exp.company_name}
                      timeInterval={`${moment(exp.start_date).format('MM.YYYY')} - ${
                        exp.end_date ? moment(exp.end_date).format('MM.YYYY') : 'Present'
                      }`}
                    />
                  ))}
              </form>
            </FormProvider>
          ) : null}
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={isNotificationOpen}
            onClose={handleNotificationClose}
            autoHideDuration={6000}
          >
            <Notification onClose={handleNotificationClose} severity="success">
              Experiences Updated Successfully!
            </Notification>
          </Snackbar>
        </FieldContainer>
      ) : null}
      <PartialLoader loading={loading} isLightContainer />
    </>
  );
};

export default Experience;
